import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormFieldTypes} from '@constants/pages/form-page/form-field-types';
import {DataType, InputField} from '@models/sidebar/response/form-page';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {FormPageService} from '@services/left-menu/page/form-page/form-page.service';


@Component({
    selector: 'scitis-form-page-input-field',
    templateUrl: './form-page-input-field.component.html',
    styleUrls: ['./form-page-input-field.component.scss'],
    standalone: false
})
export class FormPageInputFieldComponent implements OnInit, OnChanges {

  @Input()
  public subFormValidityTrigger: boolean = true;
  @Input()
  public inputField: InputField = new InputField();
  @Input()
  public inputFields: InputField[] = [];
  @Output()
  public formIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  public isTextOrNumber = true;
  public disableInputFieldNameInput: boolean = false;
  public formFieldTypes: FormFieldTypes[];
  public inputFieldForm: UntypedFormGroup = new UntypedFormGroup({});

  constructor(
      private formPageService: FormPageService
  ) {
    this.formFieldTypes = Object.values(FormFieldTypes);
  }

  get isBoolean() {
    return this.inputField.datatype === DataType.BOOLEAN;
  }

  get isString() {
    return this.inputField.datatype === DataType.STRING;
  }

  get isNumber() {
    return this.inputField.datatype === DataType.FLOAT;
  }

  get isTimestamp() {
    return this.inputField.datatype === DataType.TIMESTAMP;
  }

  get formControls() {
    return this.inputFieldForm.controls;
  }

  ngOnInit(): void {
    this.generateInputFieldForm();
    this.setPageConfigFormFieldTypes(this.inputField);
    this.formControls.dropdownOption.disable();
    if (this.inputField.columnName) {
      this.disableInputFieldNameInput = true;
    }
    this.setFlagAndEmitValidEvent(this.inputFieldForm.valid);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setFlagAndEmitValidEvent(this.inputFieldForm.valid);
  }

  public onFormFieldTypeChange($event: FormFieldTypes): void {
    this.inputField.options = [];
    this.inputField.dropdown = false;
    this.inputField.list = false;

    switch ($event) {
      case FormFieldTypes.NUMBER: {
        this.inputField.datatype = DataType.FLOAT;
        this.isTextOrNumber = true;
        break;
      }

      case FormFieldTypes.TEXT: {
        this.inputField.datatype = DataType.STRING;
        this.isTextOrNumber = true;
        break;
      }
      case FormFieldTypes.CHECKBOX: {
        this.inputField.datatype = DataType.BOOLEAN;
        this.isTextOrNumber = false;
        break;
      }
      case FormFieldTypes.DATE_AND_TIME: {
        this.inputField.datatype = DataType.TIMESTAMP;
        this.isTextOrNumber = false;
        break;
      }
    }

    this.setFlagAndEmitValidEvent(this.inputFieldForm.valid);
  }

  public toggleDropdown(): void {
    this.inputField.dropdown = !this.inputField.dropdown;
    if (this.inputField.dropdown) {
      this.formControls.dropdownOption.enable();
    } else {
      this.formControls.dropdownOption.disable();
    }
    this.setFlagAndEmitValidEvent(this.inputFieldForm.valid);
  }

  public toggleList(): void {
    this.inputField.list = !this.inputField.list;
  }

  public onAddDropdownItem(dropdownItem: HTMLInputElement): void {
    if (dropdownItem.value.trimWhiteSpaces().length > 0) {
      this.inputField.options.push(dropdownItem.value);
    }
    if (!this.isInputFieldEmpty()) {
      this.setFlagAndEmitValidEvent(this.inputFieldForm.valid);
    }
    dropdownItem.value = '';
  }

  public onDeleteDropdownItem(index: number): void {
    this.inputField.options.splice(index, 1);
    this.inputField.options = [...this.inputField.options];
    if (this.inputField.options.length === 0) {
      this.setFlagAndEmitValidEvent(false);
    }
  }

  public emitInputFieldName(): void {
    this.inputField.columnName = this.formControls.name.value;
    this.setFlagAndEmitValidEvent(this.inputFieldForm.valid);
  }

  public emitInputFieldLabel(): void {
    this.inputField.label = this.formControls.label.value;
    this.setFlagAndEmitValidEvent(this.inputFieldForm.valid);
  }

  public emitInputFieldPlaceholder(): void {
    this.inputField.placeholder = this.formControls.placeholder.value;
  }

  public onMakeFieldRequiredChange(): void {
    this.inputField.required = !this.inputField.required;
  }

  public isInputFieldEmpty(): boolean {
    return !this.inputField.options || this.inputField.options.length === 0;
  }

  private generateInputFieldForm(): void {
    this.inputFieldForm = new UntypedFormGroup({
      formFieldType: new UntypedFormControl(this.inputField.datatype, [Validators.required]),
      name: new UntypedFormControl(this.inputField.columnName, [Validators.required, this.validBigQueryNameValidator(), this.columnNameAlreadyExistsValidator()]),
      label: new UntypedFormControl(this.inputField.label, [Validators.required]),
      placeholder: new UntypedFormControl(this.inputField.placeholder),
      dropdownOption: new UntypedFormControl('', [Validators.required])
    });

    this.setFlagAndEmitValidEvent(this.inputFieldForm.valid);
  }

  private validBigQueryNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const equal = this.formPageService.bigQueryCompatibleName(control.value);
      return equal ? null : {bigQueryValidName: {value: control.value}};
    };
  }

  private columnNameAlreadyExistsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const equal = this.columnNameNotUnique(control.value);
      return equal ? {columnNameNotUnique: {value: control.value}} : null;
    };
  }

  private columnNameNotUnique(input: string){
    const inputFieldsWithoutCurrentInputField = [...this.inputFields];
    const index = this.inputFields.indexOf(this.inputField);
    if (index > -1) {
      inputFieldsWithoutCurrentInputField.splice(index, 1);
    }
    return inputFieldsWithoutCurrentInputField.some(obj => obj.columnName === input);
  }

  private setPageConfigFormFieldTypes(inputField: InputField): void {
    switch (inputField.datatype) {
      case DataType.FLOAT: {
        this.inputField.formFieldType = FormFieldTypes.NUMBER;
        break;
      }
      case DataType.STRING: {
        this.inputField.formFieldType = FormFieldTypes.TEXT;
        break;
      }
      case DataType.BOOLEAN: {
        this.inputField.formFieldType = FormFieldTypes.CHECKBOX;
        break;
      }
      case DataType.TIMESTAMP: {
        this.inputField.formFieldType = FormFieldTypes.DATE_AND_TIME;
        break;
      }
    }
  }
  private setFlagAndEmitValidEvent(valid: boolean){
    this.inputField.isValidConfiguration = valid;
    this.formIsValid.emit(valid);
  }
}
